import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { XIcon } from 'lucide-react';
import { NoteColor } from '../../types';
import { NOTE_COLORS, noteColor } from './noteColors';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
interface NoteDialogProps {
  open: boolean;
  /** Nombre del contacto, solo para dar contexto en la cabecera. */
  leadName: string;
  onClose: () => void;
  onSave: (note: {
    title?: string;
    body: string;
    color: NoteColor;
  }) => Promise<void>;
}

/**
 * Ventana flotante para escribir una nota.
 *
 * Antes la nota se escribía dentro de la propia ficha, con un campo estrecho y
 * el resto del panel compitiendo por el espacio. Ahora se abre un modal
 * centrado y cómodo con título, contenido, color y vista previa, sin salir de
 * la pantalla en la que estás. A pantalla completa en móvil.
 */
export function NoteDialog({
  open,
  leadName,
  onClose,
  onSave
}: NoteDialogProps) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [color, setColor] = useState<NoteColor>('gray');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    setTitle('');
    setBody('');
    setColor('gray');
    setError(null);
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape' && !saving) onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!open) return null;
  async function save() {
    if (!body.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({
        title: title.trim() || undefined,
        body: body.trim(),
        color
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido guardar la nota.');
    } finally {
      setSaving(false);
    }
  }
  return createPortal(<div className="fixed inset-0 z-[90] flex items-end justify-center bg-navy/40 p-0 sm:items-center sm:p-6" role="dialog" aria-modal="true" aria-label={`Escribir nota de ${leadName}`} onMouseDown={event => {
    if (event.target === event.currentTarget && !saving) onClose();
  }} data-id="element-187" data-mp="2571q5aedgyae">
      <div className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-background shadow-xl sm:rounded-2xl" data-id="element-188" data-mp="1hiiqfj9e1ttx">
        <header className="flex items-start justify-between gap-3 border-b border-border px-5 py-4" data-id="element-189" data-mp="1mj6jeah6gkqs">
          <div data-id="element-190" data-mp="17mfj7yzoj504">
            <h2 className="text-base font-semibold text-foreground" data-id="element-191" data-mp="17myzzcdlfnck">
              Escribir nota
            </h2>
            <p className="text-sm text-muted-foreground" data-id="element-192" data-mp="31yvk0k9phqxl">{leadName}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-surface" data-id="element-193" data-mp="31wss1uoztnq8">
            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-194" data-mp="v8af2xujy4u9" />
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-auto px-5 py-5" data-id="element-195" data-mp="qd3dbvkufa7z">
          <Input id="note-dialog-title" label="Título (opcional)" placeholder="Resumen de la llamada" value={title} onChange={e => setTitle(e.target.value)} data-id="element-196" data-mp="1517yr7ba5zug" />
          <Textarea id="note-dialog-body" label="Nota" rows={7} placeholder="Detalles, próximos pasos, objeciones…" value={body} onChange={e => setBody(e.target.value)} data-id="element-197" data-mp="1e0uqt2i4dtau" />

          <div data-id="element-198" data-mp="1a1c7gb5y5qa4">
            <p className="mb-2 text-sm font-medium text-foreground" data-id="element-199" data-mp="1a1m24c1xslm5">Color</p>
            <div className="flex flex-wrap gap-2" data-id="element-200" data-mp="1ny962ysb4c1k">
              {NOTE_COLORS.map(tone => <button key={tone.value} type="button" aria-label={tone.label} aria-pressed={color === tone.value} onClick={() => setColor(tone.value)} className={`flex min-h-[44px] items-center gap-2 rounded-xl border px-3 text-sm transition-colors ${color === tone.value ? 'border-foreground font-medium text-foreground' : 'border-border text-muted-foreground'} ${tone.bg}`} data-id="element-201" data-mp="1j190eozddbaz">
                  <span className={`h-3 w-3 rounded-full ${tone.dot}`} aria-hidden="true" data-id="element-202" data-mp="3r6rnow6k44d4" />
                  {tone.label}
                </button>)}
            </div>
            <div className={`mt-3 rounded-lg border-l-4 px-3 py-2 text-sm text-foreground ${noteColor(color).bg} ${noteColor(color).border}`} data-id="element-203" data-mp="3w4t419cllpkw">
              {title.trim() && <span className="block font-medium" data-id="element-204" data-mp="33jjkqkwlp7e9">{title.trim()}</span>}
              {body.trim() || 'Vista previa de la nota'}
            </div>
          </div>

          {error && <p role="alert" className="text-sm text-red-600" data-id="element-205" data-mp="3dfwax8sxt349">
              {error}
            </p>}
        </div>

        <footer className="flex flex-col gap-2 border-t border-border px-5 py-4 sm:flex-row sm:justify-end" data-id="element-206" data-mp="3i7xttdkm1s39">
          <Button variant="secondary" className="min-h-[44px] sm:min-w-[120px]" onClick={onClose} disabled={saving} data-id="element-207" data-mp="2kqpbm7lns9hy">
            Cancelar
          </Button>
          <Button className="min-h-[44px] sm:min-w-[140px]" loading={saving} disabled={!body.trim()} onClick={save} data-id="element-208" data-mp="16v79gtu78421">
            {saving ? 'Guardando…' : 'Guardar nota'}
          </Button>
        </footer>
      </div>
    </div>, document.body);
}